import { useKeycloak } from '@react-keycloak/web';
import { useCallback, useContext  } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { AppContext } from '../../../core/config';
import { useTranslation } from 'react-i18next';

const DEFAULT_WEBSOCKET_RECONNECT_INTERVAL = '5000';
const DEFAULT_WEBSOCKET_RECONNECT_MAX_ATTEMPTS = '100';

const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL_NETWORK_SERVICE_EVENTS || `wss://api/network-service-events`;
const RECONNECT_INTERVAL = parseInt(process.env.REACT_APP_WEBSOCKET_RECONNECT_INTERVAL || DEFAULT_WEBSOCKET_RECONNECT_INTERVAL, 10);
const RECONNECT_MAX_ATTEMPTS = parseInt(process.env.REACT_APP_WEBSOCKET_RECONNECT_MAX_ATTEMPTS || DEFAULT_WEBSOCKET_RECONNECT_MAX_ATTEMPTS, 10);

export const useNetworkServiceEventsApi = <T>(): {
  sendJsonMessage: (message: T) => void,
  lastMessage: MessageEvent<string> | null,
  isConnected: boolean
} => {
  const { t: translate } = useTranslation();
  const { showAlert } = useContext(AppContext);
  const { keycloak, initialized: keycloakInitialized } = useKeycloak();
  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(WEBSOCKET_URL, {
    reconnectAttempts: RECONNECT_MAX_ATTEMPTS,
    reconnectInterval: (attemptNumber: number)=> {
      console.info(`Checking WebSocket reconnect interval: attempt ${attemptNumber} of ${RECONNECT_MAX_ATTEMPTS}`);
      return RECONNECT_INTERVAL;
    },
    shouldReconnect: () => true,
    onReconnectStop: () => {
      showAlert({
        severity: 'error',
        title: translate('error.title', { ns: 'websocket' }),
        description: translate('error.description', { ns: 'websocket' }),
        autoHide: false
      });
    },
    onOpen: () => {
      console.info('WebSocket connection established.');
    },
    onClose: () => {
      console.info('WebSocket connection closed.');
    }
  });
  const token = keycloak.token;
  
  const sendAuthorizedJsonMessage = useCallback((message: T): void => {
    const authorizedMessage = keycloakInitialized ? { token, ...message } : message;
    return sendJsonMessage(authorizedMessage);
  }, [token, keycloakInitialized, sendJsonMessage]);

  return {
    sendJsonMessage: sendAuthorizedJsonMessage,
    lastMessage: lastMessage === undefined ? null : lastMessage,
    isConnected: readyState === ReadyState.OPEN
  };
};